// ticketsDisplay — the tickets hub's shared display vocabulary (ADR-073; the
// entity-display module rule, COMPONENTS §Component taxonomy): the stage badge
// + dot, the identity cell, the location cell + its compact mark (the
// assets-register grammar — merging the two into ONE composite is carded),
// the status cell with its AGENCY detail line, the visit faces, the time
// faces, the evidence count, the what-words, the recency buckets and the
// strip's chip builder. The hub, the site's Tickets tab and the peek's card
// all import it, so a face renders IDENTICALLY and evolves once. Props-only:
// the row arrives on the wire's shape, the words through the `tickets` `t`,
// the clock as `now` (the page fixes it per data arrival so a re-render never
// drifts a face).

import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { TicketFactCounts, TicketListItem, TicketLocation, TicketStageCounts, TicketVisit } from '@ems-app/schemas/tickets/responses';
import { CAPTURE_DEFAULT_LENS, TICKET_FACTS, type CaptureStage } from '@ems-app/schemas/tickets/vocabulary';
import { HISTORY_COUNT_CAP } from '@ems-app/schemas/common/pagination';
import { icons, type Icon as IconType } from '../ui/icons.js';
import { Badge, type BadgeVariant } from '../ui/Badge.js';
import { Checkbox } from '../ui/Checkbox.js';
import { InlineCopy } from '../ui/CopyField.js';
import type { QueueChip } from './QueueChipStrip.js';
import { PeriodInside } from './filters/PeriodInside.js';
import type { PeriodSpec } from './filters/types.js';
import { periodValueLabel, type PeriodContext } from '../../lib/period.js';
import { emDash, isHiddenRef } from './assetsDisplay.js';
import { EntityRef } from './EntityRef.js';
import { formatDate, formatDurationCoarse, formatDurationFine, formatNumber, formatTimeRange, knownZone } from '../../lib/format.js';
import { dayIn } from '@ems-app/schemas/common/period';

export const STAGE_BADGE: Record<CaptureStage, BadgeVariant> = {
  in_progress: 'info',
  to_review: 'warning',
  completed: 'success',
  closed: 'neutral',
};

/** The strip's stage dots — the same hue the badge wears, so chip and cell agree. */
export const STAGE_DOT: Record<CaptureStage, string> = {
  in_progress: 'bg-[var(--color-info)]',
  to_review: 'bg-[var(--color-warning)]',
  completed: 'bg-[var(--color-success)]',
  closed: 'bg-[var(--color-border-strong)]',
};

export const LOCATION_KIND_ICON: Record<TicketLocation['kind'], IconType> = {
  site: icons.site,
  warehouse: icons.warehouse,
  project: icons.building,
};

/** A machine completed the ticket at birth (a late send lands complete): the
 *  duration would lie about work that never lived as open, so Time drops it
 *  and Status names the agency (the ledger's agency law). */
export function isBornComplete(item: Pick<TicketListItem, 'created_at' | 'completed_at'>): boolean {
  return item.completed_at !== null && Date.parse(item.completed_at) - Date.parse(item.created_at) < 2 * 60_000;
}

export function isVisitOpen(visit: TicketVisit | null): boolean {
  return visit !== null && visit.checked_out_at === null;
}

/** The ticket number prints as a CODE — Latin digits, no grouping, LTR (the
 *  technical-value rule of ADR-030; a token people say on the phone). */
export function ticketNumberText(number: number): string {
  return `#${formatNumber(number, { numerals: 'latin', useGrouping: false })}`;
}

/** The moment's day in the screen's zone — the year only when it is not this year (the files rule). */
function dayText(iso: string, now: number, zone: string): string {
  const sameYear = dayIn(zone, Date.parse(iso)).slice(0, 4) === dayIn(zone, now).slice(0, 4);
  return formatDate(iso, sameYear ? 'dayMonth' : 'dayMonthYear', { timeZone: zone }) ?? '';
}

function timeText(iso: string, zone: string): string {
  return formatDate(iso, 'time', { timeZone: zone }) ?? '';
}

function distanceText(distanceM: number, t: TFunction): string {
  return distanceM < 1000
    ? t('visit.m', { value: formatNumber(distanceM, { maximumFractionDigits: 0 }) })
    : t('visit.km', { value: formatNumber(distanceM / 1000, { maximumFractionDigits: 1 }) });
}

// ── the cells ─────────────────────────────────────────────────────────────────

/** Ticket — `#n` · the TYPE word (the ROW prints the type, the peek's eyebrow
 *  the class), over the DOER (a v1 capture's technician). The first line is the
 *  row's own door to its peek where the list wires one (`onOpen` — quiet, the Tab
 *  and screen-reader path beside the row click: ADR-036 §6); without it, the fact. */
export function TicketIdentityCell({
  item,
  t,
  onOpen,
  compact = false,
}: {
  item: TicketListItem;
  t: TFunction;
  /** Open the ticket's peek (the row's own identity — a peek-only kind, so OPEN mode). */
  onOpen?: () => void;
  /** The phone face: both lines capped so the declared floor stays true (truncate alone never narrows a cell). */
  compact?: boolean;
}) {
  const cap = compact ? 'max-w-[5.5rem]' : '';
  const name = (
    <>
      <span dir="ltr" className="text-xs font-normal tabular-nums text-[var(--color-text-secondary)] group-hover/ref:text-[var(--color-info)] group-focus-visible/ref:text-[var(--color-info)]">
        {ticketNumberText(item.number)}
      </span>{' '}
      <span className="text-sm">{t(`type.${item.type}`, { defaultValue: item.type })}</span>
    </>
  );
  return (
    <div className="min-w-0">
      <div className={`flex min-w-0 ${cap}`}>
        <EntityRef quiet name={name} onOpen={onOpen} className="font-medium text-[var(--color-text-primary)]" />
      </div>
      <div className={`mt-0.5 truncate text-xs text-[var(--color-text-secondary)] ${cap}`}>{item.person?.label ?? emDash}</div>
    </div>
  );
}

/** Location — the assets-register grammar: the kind ICON (sr-worded + tooltip),
 *  the place name, the copiable reference on the quiet second line aligned
 *  under the name (a site's; a depot and the project carry none). */
export function TicketLocationCell({ item, t }: { item: TicketListItem; t: TFunction }) {
  const loc = item.location;
  const KindIcon = LOCATION_KIND_ICON[loc.kind];
  const kindWord = t(`kind.${loc.kind}`);
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="inline-flex shrink-0" title={kindWord}>
          <KindIcon size={14} aria-hidden className="text-[var(--color-text-secondary)]" />
          <span className="sr-only">{kindWord}</span>
        </span>
        <span className="truncate text-sm text-[var(--color-text-primary)]">{loc.name}</span>
      </div>
      {loc.reference && (
        <div className="mt-0.5 flex min-w-0 items-center ps-5 text-xs text-[var(--color-text-secondary)]">
          <InlineCopy value={loc.reference} />
        </div>
      )}
    </div>
  );
}

/** The location column's compact face: the kind icon ALONE, always visible —
 *  the name and the reference live one tap away in the peek. */
export function TicketLocationMark({ item, t }: { item: TicketListItem; t: TFunction }) {
  const loc = item.location;
  const KindIcon = LOCATION_KIND_ICON[loc.kind];
  const label = `${t(`kind.${loc.kind}`)} · ${loc.name}`;
  return (
    <span className="inline-flex" title={label}>
      <KindIcon size={16} aria-hidden className="text-[var(--color-text-secondary)]" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** The Status DETAIL line — Status speaks AGENCY: a machine move names itself
 *  ("Closed — 7-day grace" · "Auto-completed — no review needed"), a person's
 *  act names the person; read off the row's LAST lifecycle line. Null = the
 *  badge alone. */
export function statusDetailOf(item: TicketListItem, graceDays: number | null, t: TFunction, zone: string): string | null {
  const ev = item.last_event;
  const reopened = () => (ev?.author ? t('status.reopenedBy', { name: ev.author.label }) : t('status.reopenedLate'));
  switch (item.stage) {
    case 'closed':
      if (item.closed_by) return t('status.closedBy', { name: item.closed_by.label });
      // Filed by the grace (closed_by is NULL). The grace can have moved to
      // "never" since — then the count is unknowable and the line stays silent.
      return graceDays !== null ? t('status.closedGrace', { count: graceDays }) : null;
    case 'completed':
      if (ev?.event === 'reopened') return reopened();
      if (ev?.event === 'policy_rejudged') return t('status.policyRejudged');
      if (isBornComplete(item)) return t('status.autoCompleted');
      if (ev?.event === 'review_answered' && ev.author) return t('status.reviewedBy', { name: ev.author.label, time: timeText(ev.at, zone) });
      return t('status.noReviewNeeded');
    case 'in_progress':
      return ev?.event === 'reopened' ? reopened() : t('status.arriving');
    case 'to_review':
      if (ev?.event === 'reopened') return reopened();
      if (ev?.event === 'policy_rejudged') return t('status.policyRejudged');
      return null;
  }
}

/** Status — the stage badge (To review carries its waiting number) over the
 *  agency detail line. */
export function TicketStatusCell({ item, graceDays, t, zone }: { item: TicketListItem; graceDays: number | null; t: TFunction; /** The screen's zone (ADR-082 §2). */ zone: string }) {
  const word = item.stage === 'to_review' ? t('waiting', { count: item.waiting_count }) : t(`stage.${item.stage}`);
  const detail = statusDetailOf(item, graceDays, t, zone);
  return (
    <div className="min-w-0">
      <Badge variant={STAGE_BADGE[item.stage]}>{word}</Badge>
      {detail && <div className="mt-0.5 truncate text-xs text-[var(--color-text-secondary)]">{detail}</div>}
    </div>
  );
}

/** The status column's compact face (the phone): the badge alone. */
export function TicketStatusBadge({ item, t }: { item: TicketListItem; t: TFunction }) {
  const word = item.stage === 'to_review' ? t('waiting', { count: item.waiting_count }) : t(`stage.${item.stage}`);
  return <Badge variant={STAGE_BADGE[item.stage]}>{word}</Badge>;
}

/** Visit — the visit RECORD (ADR-073 §3): LIVE presence leads
 *  ("On site now · since 13:05"; a live away or no-GPS verdict stays loud), a
 *  CLOSED visit goes quiet and speaks ceremony-PAST — "Checked in ✓" · "· 2.3 km
 *  off" · "· no GPS" — then the explicit range and its fine duration; an
 *  EXPIRED close prints "13:10–? · out not recorded" (the presumed end never
 *  prints as real). The date joins the second line when the view is not
 *  recency-grouped (the files sizing rule). */
export function TicketVisitCell({ item, now, t, screenZone }: { item: TicketListItem; now: number; t: TFunction; /** The screen's zone (ADR-082 §2) — the visit reads in its SITE's, the word "site time" only where that differs. */ screenZone: string }) {
  const siteZone = item.location.timezone_iana;
  return <VisitFace visit={item.visit} visitsCount={item.visits_count} now={now} t={t} zone={knownZone(siteZone ?? screenZone) ?? screenZone} siteTime={!!siteZone && siteZone !== screenZone} />;
}

/** ONE visit's face — the cell's render and the peek's Visit list share it
 *  (a visit reads the same wherever it is printed). */
export function VisitFace({
  visit: v,
  visitsCount = 1,
  now,
  t,
  zone,
  siteTime = false,
}: {
  visit: TicketVisit | null;
  /** The row's history tail ("· 3 visits") — the cell shows ONE visit; the peek lists each, so it passes 1. */
  visitsCount?: number;
  now: number;
  t: TFunction;
  /** The zone the visit's clocks read in — the SITE's (a visit happened somewhere; ADR-082 §2). */
  zone: string;
  /** Say "site time" after the clocks — only where the site's zone differs from the screen's. */
  siteTime?: boolean;
}) {
  if (!v) return <span className="text-sm text-[var(--color-text-secondary)]">{t('visit.none')}</span>;

  if (v.checked_out_at === null) {
    const live =
      v.position === null
        ? { word: t('visit.checkedInNoFix'), tone: 'text-[var(--color-text-primary)]', dot: 'bg-[var(--color-border-strong)]' }
        : v.position.match
          ? { word: t('visit.onSiteNow'), tone: 'text-[var(--color-success)]', dot: 'bg-[var(--color-success)]' }
          : {
              word: t('visit.checkedInAway', { distance: distanceText(v.position.distance_m ?? 0, t) }),
              tone: 'text-[var(--color-warning)]',
              dot: 'bg-[var(--color-warning)]',
            };
    return (
      <div className="min-w-0">
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium ${live.tone}`}>
          <span aria-hidden className={`size-2 shrink-0 rounded-full ${live.dot} ring-2 ring-current/20`} />
          {live.word}
        </span>
        <div className="mt-0.5 whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
          {t('visit.since', { time: timeText(v.checked_in_at, zone) })}
          {siteTime && (
            <>
              {' '}
              <span aria-hidden>·</span> {t('visit.siteTime')}
            </>
          )}
        </div>
      </div>
    );
  }

  const qualifier =
    v.position === null ? (
      <span className="text-[var(--color-text-secondary)]">
        <span aria-hidden>·</span> {t('visit.noGps')}
      </span>
    ) : v.position.match ? (
      <span className="inline-flex text-[var(--color-success)]" title={t('visit.positionOk')}>
        <icons.check size={14} weight="bold" aria-hidden />
        <span className="sr-only">{t('visit.positionOk')}</span>
      </span>
    ) : (
      <span className="font-medium text-[var(--color-warning)]">
        <span aria-hidden>·</span> {t('visit.off', { distance: distanceText(v.position.distance_m ?? 0, t) })}
      </span>
    );

  return (
    <div className="min-w-0">
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-[var(--color-text-primary)]">
        {t('visit.checkedIn')}
        {qualifier}
        {visitsCount > 1 && (
          <span className="text-[var(--color-text-secondary)]">
            <span aria-hidden>·</span> {t('visit.visitsTail', { count: visitsCount })}
          </span>
        )}
      </span>
      <div className="mt-0.5 whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
        {/* The day always — a band is cut by the ticket's last activity, not by the visit, and spans whole weeks. */}
        {dayText(v.checked_in_at, now, zone)} <span aria-hidden>·</span>{' '}
        {/* The word sits right after the clocks it describes, before the duration and the marks. */}
        {v.closed_reason === 'expired' ? (
          <>
            <span dir="ltr">{`${timeText(v.checked_in_at, zone)}–?`}</span>
            {siteTime && (
              <>
                {' '}
                <span aria-hidden>·</span> {t('visit.siteTime')}
              </>
            )}{' '}
            <span aria-hidden>·</span> <span className="text-[var(--color-warning)]">{t('visit.outNotRecorded')}</span>
          </>
        ) : (
          <>
            <span dir="ltr">{formatTimeRange(v.checked_in_at, v.checked_out_at, { timeZone: zone })}</span>
            {siteTime && (
              <>
                {' '}
                <span aria-hidden>·</span> {t('visit.siteTime')}
              </>
            )}{' '}
            <span aria-hidden>·</span> {formatDurationFine(Date.parse(v.checked_out_at) - Date.parse(v.checked_in_at))}
          </>
        )}
      </div>
    </div>
  );
}

/** Time — ONE clock column, the most useful clock leading, the verb IN the
 *  cell: open = the age; completed = the grace countdown over the outcome;
 *  closed = the filing day over the span it took. Born-complete drops the
 *  duration (Time speaks clocks, Status speaks agency). Absolute moments ride
 *  the tooltip. */
export function TicketTimeCell({ item, now, t, zone }: { item: TicketListItem; now: number; t: TFunction; /** The screen's zone (ADR-082 §2). */ zone: string }) {
  const span = (fromMs: number, toMs: number) => formatDurationCoarse(Math.max(0, toMs - fromMs));
  const moment = (iso: string) => formatDate(iso, 'datetime', { timeZone: zone }) ?? '';
  const tip = [
    `${t('time.tip.opened')} ${moment(item.created_at)}`,
    item.completed_at ? `${t('time.tip.done')} ${moment(item.completed_at)}` : null,
    item.closed_at ? `${t('time.tip.closed')} ${moment(item.closed_at)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const wrap = (children: ReactNode) => (
    <div className="min-w-0" title={tip}>
      {children}
    </div>
  );
  const created = Date.parse(item.created_at);
  const born = isBornComplete(item);

  if (item.stage === 'closed' && item.closed_at) {
    return wrap(
      <>
        <span className="text-sm text-[var(--color-text-secondary)]">{t('time.closedOn', { day: dayText(item.closed_at, now, zone) })}</span>
        {item.completed_at && !born && (
          <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{t('time.took', { span: span(created, Date.parse(item.completed_at)) })}</div>
        )}
      </>,
    );
  }
  if (item.stage === 'completed' && item.completed_at) {
    return wrap(
      <>
        {item.grace_closes_at ? (
          <span className="text-sm text-[var(--color-text-secondary)]">{t('time.filesIn', { span: span(now, Date.parse(item.grace_closes_at)) })}</span>
        ) : (
          emDash
        )}
        {!born && <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{t('time.doneIn', { span: span(created, Date.parse(item.completed_at)) })}</div>}
      </>,
    );
  }
  // Dormant (an open ticket idle past the project's threshold): the age face takes the warning tone.
  return wrap(<span className={`text-sm ${item.dormant ? 'font-medium text-[var(--color-warning)]' : 'text-[var(--color-text-secondary)]'}`}>{t('time.age', { span: span(created, now) })}</span>);
}

/** Evidence — the ticket's files, both natures, as a count. */
export function TicketEvidenceCell({ item }: { item: TicketListItem }) {
  if (item.evidence_count === 0) return emDash;
  return (
    <span className="inline-flex items-center gap-1 text-sm tabular-nums text-[var(--color-text-primary)]">
      <icons.media size={16} aria-hidden className="text-[var(--color-text-secondary)]" />
      {formatNumber(item.evidence_count)}
    </span>
  );
}

/** The What words — the confirmed subjects and the topics across the ticket's
 *  files, joined; the overflow as "+n"; nothing yet = the quiet placeholder. */
export function ticketWhatText(item: Pick<TicketListItem, 'what'>, topicLabel: (topic: string) => string, t: TFunction): string | null {
  const words = [...item.what.subjects.map((s) => (isHiddenRef(s) ? t(`hidden.${s.kind}`, { ns: 'assets' }) : s.label)), ...item.what.topics.map(topicLabel)];
  if (words.length === 0) return null;
  return item.what.more > 0 ? `${words.join(' · ')} · ${t('what.more', { count: item.what.more })}` : words.join(' · ');
}

export function TicketWhatCell({ item, topicLabel, t }: { item: TicketListItem; topicLabel: (topic: string) => string; t: TFunction }) {
  const text = ticketWhatText(item, topicLabel, t);
  if (!text) return <span className="text-sm text-[var(--color-text-disabled)]">{t('what.notClassified')}</span>;
  return (
    <div className="min-w-0">
      <span className="line-clamp-2 text-sm text-[var(--color-text-primary)]">{text}</span>
    </div>
  );
}

// ── the strip ─────────────────────────────────────────────────────────────────

/** The chip keys carry their group so one `active` list serves the strip. */
export const stageChipKey = (s: CaptureStage) => `stage:${s}`;
export const factChipKey = (f: (typeof TICKET_FACTS)[number]) => `fact:${f}`;
/** The Closed chip — a MENU chip (ADR-078 §2): pressed look while a window is open; its inside writes the window. */
export const CLOSED_CHIP_KEY = 'lens:closed';

/** What the Closed chip prints and writes: the window as the URL carries it (null = hidden — nothing filed is read or
 *  counted), "only closed" (`stage=closed`), the period spec its inside reads, and the two writes. */
export interface ClosedChipState {
  value: string | null;
  only: boolean;
  spec: PeriodSpec;
  onChange: (value: string | null) => void;
  onOnly: (on: boolean) => void;
}

/** The strip (ADR-073 §7 · ADR-078 §2): the three STAGE chips are narrowing
 *  filters over the OPEN work — nothing pressed = every row of it, each count
 *  exempting its own axis; the CLOSED chip is a MENU chip at the group's end —
 *  hidden by default (nothing filed is read or counted), a press opens the
 *  windows on the closing time (the words · Custom…) and the "Only
 *  closed tickets" tick, the chip printing the state ("Closed · Last 30 days ·
 *  140", "1,000+" past the cap). The FACTS group follows: a chip only while
 *  its count is above zero OR it is pressed (an active chip never disappears),
 *  in the reach order. To review's count wears the warning tone (the one
 *  actionable signal). Asana's "show completed" is the precedent — a widening
 *  window beside narrowing filters, never a filter pressed by default. */
export function ticketChips({
  stageCounts,
  factCounts,
  active,
  closed,
  t,
  tc,
}: {
  stageCounts: TicketStageCounts;
  factCounts: TicketFactCounts;
  active: readonly string[];
  closed: ClosedChipState;
  t: TFunction;
  /** The common namespace — the period words. */
  tc: TFunction;
}): QueueChip[] {
  const ctx: PeriodContext = { zone: closed.spec.zone, weekStart: closed.spec.weekStart, now: closed.spec.now };
  const state = closed.value === null ? t('strip.closedHidden') : periodValueLabel(closed.value, ctx, tc);
  return [
    ...CAPTURE_DEFAULT_LENS.map((s) => ({
      key: stageChipKey(s),
      label: t(`stage.${s}`),
      count: stageCounts[s],
      dotClassName: STAGE_DOT[s],
      emphasize: s === 'to_review',
      group: 'stages',
    })),
    {
      key: CLOSED_CHIP_KEY,
      // The label says the window alone — the tick's state is the menu's and the view's (a trailing "only" read as a qualifier of the number).
      label: `${t('stage.closed')} · ${state}`,
      count: stageCounts.closed,
      cap: HISTORY_COUNT_CAP,
      dotClassName: STAGE_DOT.closed,
      group: 'lens',
      menu: {
        label: t('strip.closedMenu'),
        inside: (close, face) => <ClosedInside closed={closed} face={face} onDone={close} t={t} />,
        // The family's ×: a set window is cleared from the chip itself (the architect's eyeball — no "Hidden" row in the menu).
        clear: closed.value === null ? undefined : { label: t('strip.hideClosed'), onClear: () => closed.onChange(null) },
      },
    },
    ...TICKET_FACTS.filter((f) => factCounts[f] > 0 || active.includes(factChipKey(f))).map((f) => ({
      key: factChipKey(f),
      label: t(`fact.${f}`),
      count: factCounts[f],
      group: 'facts',
    })),
  ];
}

/** The Closed chip's inside: the family's period picker over the closing time (the hub's words — 7 days first — Custom… last) · the
 *  "Only closed tickets" tick. A pick closes the panel; the chip prints what was written and wears the × to clear it
 *  (a re-press of the chosen row clears too — the filter's rule). */
function ClosedInside({ closed, face, onDone, t }: { closed: ClosedChipState; face: 'desk' | 'phone'; onDone: () => void; t: TFunction }) {
  const touch = face === 'phone';
  const hidden = closed.value === null;
  return (
    <div className={touch ? 'flex flex-col' : 'flex max-h-[min(28rem,calc(100dvh-5rem))] flex-col p-1.5'}>
      <PeriodInside
        spec={closed.spec}
        label={t('stage.closed')}
        value={hidden ? [] : [closed.value!]}
        onChange={(next) => closed.onChange(next[0] ?? null)}
        onDone={onDone}
        rowSize={touch ? 'touch' : 'default'}
      />
      <div aria-hidden className="my-1 h-px bg-[var(--color-border)]" />
      <div className={touch ? 'px-4 py-3' : 'px-2.5 py-1.5'}>
        <Checkbox
          checked={closed.only}
          onChange={(on) => {
            closed.onOnly(on);
            onDone();
          }}
          label={t('strip.onlyClosed')}
        />
      </div>
    </div>
  );
}
